/**
 * Trace Studio panel — Projects tab: team projects and their scenes, plus
 * the synthetic "Independent Scenes" bucket. Handles scene creation,
 * publish-status changes, and location filtering/sorting.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Teams, Projects, Scenes, AccessType, AnchorType } from 'trace-viewer/cms';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import {
    styles,
    apiConfig,
    doOpenScene,
    sceneHasContent,
    ConfirmOpenBar,
    ConfirmDeleteBar,
    CreateSceneForm,
    InlineNameEdit,
    RenameButton,
    DeleteButton,
    NewSceneCard,
    ProjectCard,
    SceneCard,
    SceneInfoBar,
    DateRows,
    EntityDetails,
    QrCode,
    shareUrl,
    PublishStatusSelect,
    updateSceneStatus,
    updateProjectStatus,
    renameScene,
    deleteScene,
    renameProject,
    deleteProject,
    extractServerErrorMessage,
    type PublishStatus,
    Spacers,
} from '../catalog-shared';
import {
    FilterSortBar,
    applyLocationFilter,
    sortEntries,
    type LocationFilter,
    type SortKey,
} from '../catalog-filters';
import { showSpinnerToast, showToast } from '../toast';
import { cachedSlot, useMirrorSlot, useSharedTeam, teamScopedSlot, useMirrorTeamScopedSlot, useCatalogRefresh } from './state';

/** Sentinel ID for the synthetic "Independent Scenes" project card. */
const INDEPENDENT_SCENES_ID = '__independent__';

export const ProjectsTab: React.FC<{ token: string; active: boolean }> = ({ token, active }) => {
    const [selectedTeamId] = useSharedTeam();
    const [projects, setProjects] = useState<ProjectResponseDto[]>(
        () => teamScopedSlot('projects.projects', selectedTeamId, [] as ProjectResponseDto[]),
    );
    const [independentScenes, setIndependentScenes] = useState<SceneResponseDto[]>(
        () => teamScopedSlot('projects.independentScenes', selectedTeamId, [] as SceneResponseDto[]),
    );
    /** projectId → attached-scene count, derived from the team scenes list (no extra query). */
    const [sceneCounts, setSceneCounts] = useState<Record<string, number>>(
        () => teamScopedSlot('projects.sceneCounts', selectedTeamId, {}),
    );
    const [selectedProject, setSelectedProject] = useState<ProjectResponseDto | null>(
        () => teamScopedSlot('projects.selectedProject', selectedTeamId, null as ProjectResponseDto | null),
    );
    const [scenes, setScenes] = useState<SceneResponseDto[]>(
        () => teamScopedSlot('projects.scenes', selectedTeamId, [] as SceneResponseDto[]),
    );
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('ready');
    const [errorMsg, setErrorMsg] = useState('');
    const [insertingSceneId, setInsertingSceneId] = useState<string | null>(null);
    const [selectedSceneId, setSelectedSceneId] = useState<string | null>(
        () => teamScopedSlot('projects.selectedSceneId', selectedTeamId, null as string | null),
    );
    // Search is split per navigation level so drilling into a project and
    // backing out preserves the project-list filter (and vice versa).
    const [search, setSearch] = useState<string>(
        () => teamScopedSlot('projects.search', selectedTeamId, ''),
    );
    const [sceneSearch, setSceneSearch] = useState<string>(
        () => teamScopedSlot('projects.sceneSearch', selectedTeamId, ''),
    );
    // Filter/sort are user preferences, not team data — plain (non-scoped) slots.
    const [locationFilter, setLocationFilter] = useState<LocationFilter>(
        () => cachedSlot<LocationFilter>('projects.locationFilter', 'all'),
    );
    const [sortKey, setSortKey] = useState<SortKey>(
        () => cachedSlot<SortKey>('projects.sortKey', 'updated'),
    );
    useMirrorSlot('projects.locationFilter', locationFilter);
    useMirrorSlot('projects.sortKey', sortKey);
    useMirrorTeamScopedSlot('projects.projects', selectedTeamId, projects);
    useMirrorTeamScopedSlot('projects.independentScenes', selectedTeamId, independentScenes);
    useMirrorTeamScopedSlot('projects.sceneCounts', selectedTeamId, sceneCounts);
    useMirrorTeamScopedSlot('projects.selectedProject', selectedTeamId, selectedProject);
    useMirrorTeamScopedSlot('projects.scenes', selectedTeamId, scenes);
    useMirrorTeamScopedSlot('projects.selectedSceneId', selectedTeamId, selectedSceneId);
    useMirrorTeamScopedSlot('projects.search', selectedTeamId, search);
    useMirrorTeamScopedSlot('projects.sceneSearch', selectedTeamId, sceneSearch);
    const [loadingScenes, setLoadingScenes] = useState(false);
    const teamsRef = useRef<Teams | null>(null);
    const projectsRef = useRef<Projects | null>(null);
    const scenesRef = useRef<Scenes | null>(null);
    const [confirmingOpen, setConfirmingOpen] = useState(false);
    const [publishBusyId, setPublishBusyId] = useState<string | null>(null);
    const [creatingScene, setCreatingScene] = useState(false);
    const [createBusy, setCreateBusy] = useState(false);
    /** Scene/project id with a rename or delete in flight. */
    const [mutatingId, setMutatingId] = useState<string | null>(null);
    const [confirmDeleteSceneId, setConfirmDeleteSceneId] = useState<string | null>(null);
    const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);
    const [editingProjectName, setEditingProjectName] = useState(false);
    useEffect(() => { setConfirmingOpen(false); setConfirmDeleteSceneId(null); }, [selectedSceneId]);
    useEffect(() => { setCreatingScene(false); setConfirmDeleteProject(false); setEditingProjectName(false); }, [selectedProject]);

    // Re-hydrate from the team-scoped cache whenever the active team changes.
    // Initial state was already seeded by the useState initializers above.
    const firstTeamHydrate = useRef(true);
    useEffect(() => {
        if (firstTeamHydrate.current) { firstTeamHydrate.current = false; return; }
        setProjects(teamScopedSlot('projects.projects', selectedTeamId, [] as ProjectResponseDto[]));
        setIndependentScenes(teamScopedSlot('projects.independentScenes', selectedTeamId, [] as SceneResponseDto[]));
        setSceneCounts(teamScopedSlot('projects.sceneCounts', selectedTeamId, {}));
        setSelectedProject(teamScopedSlot('projects.selectedProject', selectedTeamId, null as ProjectResponseDto | null));
        setScenes(teamScopedSlot('projects.scenes', selectedTeamId, [] as SceneResponseDto[]));
        setSelectedSceneId(teamScopedSlot('projects.selectedSceneId', selectedTeamId, null as string | null));
        setSearch(teamScopedSlot('projects.search', selectedTeamId, ''));
        setSceneSearch(teamScopedSlot('projects.sceneSearch', selectedTeamId, ''));
    }, [selectedTeamId]);

    /**
     * Digest a full team scenes list into the independent-scene bucket and the
     * per-project attached-scene counts. Returns the independents for callers
     * that also need them as the visible grid.
     */
    const deriveTeamScenes = useCallback((all: SceneResponseDto[]): SceneResponseDto[] => {
        const independents = all.filter(s => !s.isAttached && !s.projectId);
        const counts: Record<string, number> = {};
        for (const s of all) {
            if (s.projectId) counts[s.projectId] = (counts[s.projectId] ?? 0) + 1;
        }
        setIndependentScenes(independents);
        setSceneCounts(counts);
        return independents;
    }, []);

    const handleProjectPublishChange = useCallback(async (project: ProjectResponseDto, next: PublishStatus) => {
        if (!project.id || !selectedTeamId) return;
        setPublishBusyId(project.id);
        try {
            const updated = await updateProjectStatus(token, project, selectedTeamId, next);
            setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
            setSelectedProject(prev => prev && prev.id === updated.id ? updated : prev);
            // Backend cascades project status to attached scenes — refresh local list.
            if (selectedProject?.id === project.id && project.id !== INDEPENDENT_SCENES_ID) {
                try {
                    const res = await projectsRef.current!.scenesList(project.id);
                    setScenes(res.data ?? []);
                } catch (err) {
                    console.error('[Projects] Refresh scenes after publish change failed:', err);
                }
            }
            showToast(`Project set to ${next}`, 'success');
        } catch (err) {
            console.error('[Projects] Update project status failed:', err);
            const msg = extractServerErrorMessage(err);
            showToast(msg ?? 'Failed to update access', 'error');
        } finally {
            setPublishBusyId(null);
        }
    }, [token, selectedTeamId, selectedProject]);

    const handleScenePublishChange = useCallback(async (scene: SceneResponseDto, next: PublishStatus) => {
        if (!scene.id || !selectedTeamId) return;
        setPublishBusyId(scene.id);
        try {
            const updated = await updateSceneStatus(token, scene, selectedTeamId, next);
            setScenes(prev => prev.map(s => s.id === updated.id ? updated : s));
            setIndependentScenes(prev => prev.map(s => s.id === updated.id ? updated : s));
            showToast(`Scene set to ${next}`, 'success');
        } catch (err) {
            console.error('[Projects] Update scene status failed:', err);
            const msg = extractServerErrorMessage(err);
            showToast(msg ?? 'Failed to update access', 'error');
        } finally {
            setPublishBusyId(null);
        }
    }, [token, selectedTeamId]);

    // Hydrate API client refs on every mount.
    useEffect(() => {
        teamsRef.current = new Teams(apiConfig(token));
        projectsRef.current = new Projects(apiConfig(token));
        scenesRef.current = new Scenes(apiConfig(token));
    }, [token]);

    // Fetch projects and independent scenes whenever the team changes (and on
    // mount). When the team-scoped cache has data it stays visible while the
    // fetch refreshes it in the background (stale-while-revalidate); the
    // loading/error states only show when there is nothing cached. Read the
    // slots directly to avoid a closure race with the team-change
    // re-hydration effect above.
    useEffect(() => {
        if (!selectedTeamId) return;
        const hasCache =
            teamScopedSlot('projects.projects', selectedTeamId, [] as ProjectResponseDto[]).length > 0 ||
            teamScopedSlot('projects.independentScenes', selectedTeamId, [] as SceneResponseDto[]).length > 0;
        if (hasCache) setStatus('ready');
        else setStatus('loading');
        let cancelled = false;
        void (async () => {
            try {
                const [projectsRes, scenesRes] = await Promise.all([
                    teamsRef.current!.projectsList(selectedTeamId),
                    teamsRef.current!.scenesList(selectedTeamId),
                ]);
                if (cancelled) return;
                setProjects(projectsRes.data ?? []);
                const independents = deriveTeamScenes(scenesRes.data ?? []);
                setStatus('ready');

                // Keep an open (cached) scene list in sync with the refresh.
                const cachedProject = teamScopedSlot('projects.selectedProject', selectedTeamId, null as ProjectResponseDto | null);
                if (cachedProject?.id === INDEPENDENT_SCENES_ID) {
                    setScenes(independents);
                } else if (cachedProject?.id) {
                    try {
                        const res = await projectsRef.current!.scenesList(cachedProject.id);
                        if (!cancelled) setScenes(res.data ?? []);
                    } catch (err) {
                        console.error('[Projects] Refresh open project scenes failed:', err);
                    }
                }
            } catch (err) {
                console.error('[Projects] Failed to fetch projects:', err);
                if (!cancelled && !hasCache) {
                    setProjects([]);
                    setIndependentScenes([]);
                    setErrorMsg(err instanceof Error ? err.message : 'Failed to load projects');
                    setStatus('error');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [selectedTeamId, deriveTeamScenes]);

    // NOTE: also called by refresh paths (refresh button, post-publish) —
    // must not reset UI state like sceneSearch; the project-card click site
    // clears that.
    const openProject = useCallback(async (project: ProjectResponseDto) => {
        if (!projectsRef.current || !project.id) return;
        setSelectedProject(project);
        setLoadingScenes(true);
        try {
            const res = await projectsRef.current.scenesList(project.id);
            setScenes(res.data ?? []);
        } catch (err) {
            console.error('[Projects] Failed to fetch scenes:', err);
            setScenes([]);
        } finally {
            setLoadingScenes(false);
        }
    }, []);

    const openIndependentScenes = useCallback(() => {
        setSceneSearch('');
        setSelectedProject({ id: INDEPENDENT_SCENES_ID, name: 'Independent Scenes' });
        setScenes(independentScenes);
    }, [independentScenes]);

    const refreshProjectList = useCallback(async () => {
        if (!selectedTeamId || !teamsRef.current) return;
        try {
            const [projectsRes, scenesRes] = await Promise.all([
                teamsRef.current.projectsList(selectedTeamId),
                teamsRef.current.scenesList(selectedTeamId),
            ]);
            setProjects(projectsRes.data ?? []);
            deriveTeamScenes(scenesRes.data ?? []);
        } catch (err) {
            console.error('[Projects] Refresh projects failed:', err);
        }
    }, [selectedTeamId, deriveTeamScenes]);

    const handleSceneRename = useCallback(async (scene: SceneResponseDto, name: string) => {
        if (!scene.id || !selectedTeamId) return;
        setMutatingId(scene.id);
        const dismiss = showSpinnerToast(`Renaming "${scene.name ?? 'scene'}"...`);
        try {
            const updated = await renameScene(token, scene.id, selectedTeamId, name);
            setScenes(prev => prev.map(s => s.id === updated.id ? updated : s));
            setIndependentScenes(prev => prev.map(s => s.id === updated.id ? updated : s));
            dismiss(`Renamed to "${name}"`, 'success');
        } catch (err) {
            console.error('[Projects] Rename scene failed:', err);
            dismiss(extractServerErrorMessage(err) ?? 'Rename failed', 'error');
        } finally {
            setMutatingId(null);
        }
    }, [token, selectedTeamId]);

    const handleSceneDelete = useCallback(async (scene: SceneResponseDto) => {
        if (!scene.id || !selectedTeamId) return;
        setMutatingId(scene.id);
        const dismiss = showSpinnerToast(`Deleting "${scene.name ?? 'scene'}"...`);
        try {
            await deleteScene(token, scene.id, selectedTeamId);
            setScenes(prev => prev.filter(s => s.id !== scene.id));
            setIndependentScenes(prev => prev.filter(s => s.id !== scene.id));
            setSelectedSceneId(null);
            setConfirmDeleteSceneId(null);
            dismiss(`Deleted "${scene.name ?? 'scene'}"`, 'success');
            // Deleting a project's last scene deletes the project server-side —
            // quietly refresh the project list and scene counts.
            void refreshProjectList();
        } catch (err) {
            console.error('[Projects] Delete scene failed:', err);
            dismiss(extractServerErrorMessage(err) ?? 'Delete failed', 'error');
        } finally {
            setMutatingId(null);
        }
    }, [token, selectedTeamId, refreshProjectList]);

    const handleProjectRename = useCallback(async (name: string) => {
        if (!selectedProject?.id || selectedProject.id === INDEPENDENT_SCENES_ID || !selectedTeamId) return;
        setMutatingId(selectedProject.id);
        const dismiss = showSpinnerToast(`Renaming "${selectedProject.name ?? 'project'}"...`);
        try {
            const updated = await renameProject(token, selectedProject.id, selectedTeamId, name);
            setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
            setSelectedProject(updated);
            dismiss(`Renamed to "${name}"`, 'success');
        } catch (err) {
            console.error('[Projects] Rename project failed:', err);
            dismiss(extractServerErrorMessage(err) ?? 'Rename failed', 'error');
        } finally {
            setMutatingId(null);
            setEditingProjectName(false);
        }
    }, [token, selectedTeamId, selectedProject]);

    const handleProjectDelete = useCallback(async () => {
        if (!selectedProject?.id || selectedProject.id === INDEPENDENT_SCENES_ID || !selectedTeamId) return;
        setMutatingId(selectedProject.id);
        const dismiss = showSpinnerToast(`Deleting "${selectedProject.name ?? 'project'}"...`);
        try {
            await deleteProject(token, selectedProject.id, selectedTeamId);
            dismiss(`Deleted "${selectedProject.name ?? 'project'}"`, 'success');
            setConfirmDeleteProject(false);
            setSelectedProject(null);
            setScenes([]);
            setSelectedSceneId(null);
            void refreshProjectList();
        } catch (err) {
            console.error('[Projects] Delete project failed:', err);
            dismiss(extractServerErrorMessage(err) ?? 'Delete failed', 'error');
        } finally {
            setMutatingId(null);
        }
    }, [token, selectedTeamId, selectedProject, refreshProjectList]);

    const refreshCurrent = useCallback(async () => {
        if (selectedProject) {
            if (selectedProject.id === INDEPENDENT_SCENES_ID) {
                if (!selectedTeamId || !teamsRef.current) return;
                try {
                    const res = await teamsRef.current.scenesList(selectedTeamId);
                    setScenes(deriveTeamScenes(res.data ?? []));
                } catch (err) {
                    console.error('[Projects] Refresh independent scenes failed:', err);
                }
            } else {
                await openProject(selectedProject);
            }
        } else {
            await refreshProjectList();
        }
    }, [selectedProject, selectedTeamId, openProject, refreshProjectList, deriveTeamScenes]);
    useCatalogRefresh(active, refreshCurrent);

    const handleCreateScene = useCallback(async (params: { name: string; image: File | null; imageWidth: number | null }) => {
        if (!selectedTeamId || !scenesRef.current || !selectedProject) return;
        const isIndependentBucket = selectedProject.id === INDEPENDENT_SCENES_ID;
        const isRealProject = !!selectedProject.id && !isIndependentBucket;
        setCreateBusy(true);
        const dismiss = showSpinnerToast(`Creating scene "${params.name}"...`);
        try {
            const createReq: Parameters<Scenes['scenesCreate']>[0] = {
                Name: params.name,
                TeamId: selectedTeamId,
                ActivationStatus: 'UnPublished',
                AccessType: AccessType.Anywhere,
                AnchorType: params.image ? AnchorType.Image : AnchorType.Local,
            };
            if (params.image) {
                createReq.Image = params.image;
                if (params.imageWidth !== null) createReq.ImageWidth = params.imageWidth;
            }
            const createRes = await scenesRef.current.scenesCreate(createReq);
            const created = createRes.data;
            if (!created?.id) throw new Error('Scene creation returned no id');

            if (isRealProject && selectedProject.id) {
                await projectsRef.current!.scenesPartialUpdate2(selectedProject.id, created.id, { teamId: selectedTeamId });
            }

            if (isRealProject && selectedProject.id) {
                const res = await projectsRef.current!.scenesList(selectedProject.id);
                setScenes(res.data ?? []);
            } else {
                const res = await teamsRef.current!.scenesList(selectedTeamId);
                setScenes(deriveTeamScenes(res.data ?? []));
            }

            setSelectedSceneId(created.id);
            setCreatingScene(false);
            dismiss(`Created "${params.name}"`, 'success');
        } catch (err) {
            console.error('[Projects] Create scene failed:', err);
            dismiss('Failed to create scene', 'error');
        } finally {
            setCreateBusy(false);
        }
    }, [selectedTeamId, selectedProject, deriveTeamScenes]);

    const requestOpenScene = useCallback((scene: SceneResponseDto) => {
        if (sceneHasContent()) {
            setConfirmingOpen(true);
        } else {
            void doOpenScene(scene, setInsertingSceneId);
        }
    }, []);

    const selectedScene = useMemo(
        () => selectedSceneId ? scenes.find(s => s.id === selectedSceneId) ?? null : null,
        [selectedSceneId, scenes],
    );

    const projectQuery = search.toLowerCase().trim();
    const sceneQuery = sceneSearch.toLowerCase().trim();

    const filteredProjects = useMemo(() => {
        const searched = projectQuery
            ? projects.filter(p =>
                (p.name?.toLowerCase().includes(projectQuery) ?? false) ||
                (p.description?.toLowerCase().includes(projectQuery) ?? false))
            : projects;
        return sortEntries(applyLocationFilter(searched, locationFilter, p => p.projectAccessors), sortKey);
    }, [projects, projectQuery, locationFilter, sortKey]);

    // Always offered (even when empty) — it's where independent scenes get created.
    const showIndependentCard = !projectQuery || 'independent scenes'.includes(projectQuery);

    const filteredScenes = useMemo(() => {
        const searched = sceneQuery ? scenes.filter(s => s.name?.toLowerCase().includes(sceneQuery)) : scenes;
        return sortEntries(applyLocationFilter(searched, locationFilter, s => s.sceneAccessors), sortKey);
    }, [scenes, sceneQuery, locationFilter, sortKey]);

    const projectNarrowing = !!projectQuery || locationFilter !== 'all';
    const sceneNarrowing = !!sceneQuery || locationFilter !== 'all';

    if (!selectedTeamId) {
        return <div style={styles.center}>No team selected</div>;
    }

    if (status === 'loading') {
        return <div style={styles.center}>Loading projects...</div>;
    }

    if (status === 'error') {
        return (
            <div style={styles.center}>
                <div>{errorMsg || 'Failed to load projects'}</div>
            </div>
        );
    }

    if (selectedProject) {
        const isIndependentBucket = selectedProject.id === INDEPENDENT_SCENES_ID;
        const isRealProject = !!selectedProject.id && !isIndependentBucket;
        const selectedSceneIsIndependent = selectedScene
            ? (!selectedScene.isAttached && !selectedScene.projectId)
            : false;
        return (
            <>
                <div style={styles.header}>
                    <button style={styles.backButton} onClick={() => { setSelectedProject(null); setScenes([]); setSelectedSceneId(null); setConfirmingOpen(false); }} title="Back to projects">&#8592;</button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {editingProjectName && isRealProject ? (
                            <InlineNameEdit
                                initial={selectedProject.name ?? ''}
                                busy={mutatingId === selectedProject.id}
                                onCommit={name => { void handleProjectRename(name); }}
                                onCancel={() => setEditingProjectName(false)}
                            />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                                <div style={{ ...styles.title, minWidth: 0 }}>{selectedProject.name ?? 'Project'}</div>
                                {isRealProject && (
                                    <RenameButton
                                        onClick={() => setEditingProjectName(true)}
                                        disabled={mutatingId === selectedProject.id}
                                        title="Rename project"
                                    />
                                )}
                            </div>
                        )}
                        <EntityDetails shortCode={selectedProject.shortCode} accessors={selectedProject.projectAccessors} />
                        {isRealProject && <DateRows dateCreated={selectedProject.dateCreated} dateModified={selectedProject.dateModified} />}
                        {isRealProject && (
                            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center' }}>
                                <PublishStatusSelect
                                    value={selectedProject.activationStatus}
                                    busy={publishBusyId === selectedProject.id}
                                    onChange={next => { void handleProjectPublishChange(selectedProject, next); }}
                                    title="Cascades to all scenes in this project"
                                />
                                <DeleteButton
                                    onClick={() => setConfirmDeleteProject(true)}
                                    busy={mutatingId === selectedProject.id}
                                    disabled={mutatingId === selectedProject.id}
                                    title="Delete project"
                                />
                            </div>
                        )}
                    </div>
                    {selectedProject.shortCode && (
                        <QrCode
                            url={shareUrl('projects', selectedProject.shortCode)}
                            size={48}
                            title={`Open ${shareUrl('projects', selectedProject.shortCode)}`}
                        />
                    )}
                </div>
                {confirmDeleteProject && isRealProject && (
                    <ConfirmDeleteBar
                        name={selectedProject.name ?? 'project'}
                        busy={mutatingId === selectedProject.id}
                        onConfirm={() => { void handleProjectDelete(); }}
                        onCancel={() => setConfirmDeleteProject(false)}
                    />
                )}
                <input type="text" placeholder="Search scenes..." value={sceneSearch} onChange={e => setSceneSearch(e.target.value)} style={styles.searchInput} />
                <FilterSortBar filter={locationFilter} onFilterChange={setLocationFilter} sort={sortKey} onSortChange={setSortKey} />
                {creatingScene && (
                    <CreateSceneForm
                        busy={createBusy}
                        onCreate={(params) => { void handleCreateScene(params); }}
                        onCancel={() => setCreatingScene(false)}
                    />
                )}
                {selectedScene && (
                    <>
                        <SceneInfoBar
                            scene={selectedScene}
                            token={token}
                            onOpen={() => requestOpenScene(selectedScene)}
                            onDeselect={() => { setSelectedSceneId(null); setConfirmingOpen(false); }}
                            busy={insertingSceneId === selectedScene.id}
                            publishControl={selectedSceneIsIndependent ? (
                                <PublishStatusSelect
                                    value={selectedScene.activationStatus}
                                    busy={publishBusyId === selectedScene.id}
                                    onChange={next => { void handleScenePublishChange(selectedScene, next); }}
                                />
                            ) : undefined}
                            onRename={name => { void handleSceneRename(selectedScene, name); }}
                            renaming={mutatingId === selectedScene.id}
                            onDelete={() => setConfirmDeleteSceneId(selectedScene.id ?? null)}
                            deleting={mutatingId === selectedScene.id}
                        />
                        {confirmingOpen && (
                            <ConfirmOpenBar
                                name={selectedScene.name ?? 'scene'}
                                onConfirm={() => { setConfirmingOpen(false); void doOpenScene(selectedScene, setInsertingSceneId); }}
                                onCancel={() => setConfirmingOpen(false)}
                            />
                        )}
                        {confirmDeleteSceneId === selectedScene.id && (
                            <ConfirmDeleteBar
                                name={selectedScene.name ?? 'scene'}
                                busy={mutatingId === selectedScene.id}
                                onConfirm={() => { void handleSceneDelete(selectedScene); }}
                                onCancel={() => setConfirmDeleteSceneId(null)}
                            />
                        )}
                    </>
                )}
                {loadingScenes ? (
                    <div style={styles.center}>Loading scenes...</div>
                ) : filteredScenes.length === 0 && sceneNarrowing ? (
                    <div style={styles.center}>No matching scenes</div>
                ) : (
                    <div style={styles.flexGrid}>
                        {!sceneQuery && !creatingScene && <NewSceneCard onClick={() => setCreatingScene(true)} />}
                        {filteredScenes.map(scene => (
                            <SceneCard
                                key={scene.id}
                                scene={scene}
                                onSelect={() => setSelectedSceneId(scene.id ?? null)}
                                selected={scene.id === selectedSceneId}
                                inserting={insertingSceneId === scene.id}
                            />
                        ))}
                        <Spacers />
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
            <FilterSortBar filter={locationFilter} onFilterChange={setLocationFilter} sort={sortKey} onSortChange={setSortKey} />
            {filteredProjects.length === 0 && !showIndependentCard ? (
                <div style={styles.center}>{projectNarrowing ? 'No matching projects' : 'No projects'}</div>
            ) : (
                <div style={styles.flexGrid}>
                    {showIndependentCard && (
                        <ProjectCard
                            project={{ name: 'Independent Scenes' }}
                            sceneCount={independentScenes.length}
                            onClick={openIndependentScenes}
                        />
                    )}
                    {filteredProjects.map(project => (
                        <ProjectCard
                            key={project.id}
                            project={project}
                            sceneCount={project.scenes?.length ?? sceneCounts[project.id ?? ''] ?? 0}
                            onClick={() => { setSceneSearch(''); void openProject(project); }}
                        />
                    ))}
                    <Spacers />
                </div>
            )}
        </>
    );
};
